import { Controller } from "@hotwired/stimulus"

// Generalized conditional-visibility controller.
//
// A card declares either:
//   data-condition='[{"field":"form[a]","in":["yes"]},{"field":"form[b][]","in":["x","y"]}]'
//     → visible when ALL conditions match (each condition matches if any current
//       value of `field` is in `in`).
//   data-show-for / data-show-when (legacy single-condition form).
//
// Conditional-required inputs use data-required; they become required only while visible.
//
// Inputs inside a hidden card are also disabled so values from an abandoned
// branch never submit. Only inputs this controller disabled (marked with
// data-condition-disabled) are re-enabled on show — inputs disabled for other
// reasons (read mode) are left alone.
export default class extends Controller {
  static targets = ["card"]

  connect() {
    this.boundEvaluate = () => this.evaluate()
    this.element.addEventListener("change", this.boundEvaluate)
    this.element.addEventListener("input", this.boundEvaluate)
    this.evaluate()
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundEvaluate)
    this.element.removeEventListener("input", this.boundEvaluate)
  }

  // Kept so existing data-action="change->form-conditions#update" still works.
  update() {
    this.evaluate()
  }

  evaluate() {
    this.cardTargets.forEach(card => {
      const conditions = this.#conditionsFor(card)
      const visible = conditions.every(c => this.#matches(c))
      visible ? this.#show(card) : this.#hide(card)
    })
  }

  #conditionsFor(card) {
    if (card.dataset.condition) {
      try {
        return JSON.parse(card.dataset.condition)
      } catch (e) {
        console.error("form-conditions: bad data-condition", card, e)
        return []
      }
    }
    if (card.dataset.showFor) {
      return [{ field: card.dataset.showFor, in: [card.dataset.showWhen] }]
    }
    return []
  }

  #matches(condition) {
    const values = this.#currentValues(condition.field)
    return values.some(v => condition.in.includes(v))
  }

  #currentValues(name) {
    const inputs = this.element.querySelectorAll(`[name="${CSS.escape(name)}"]`)
    const values = []
    inputs.forEach(input => {
      if (input.type === "radio" || input.type === "checkbox") {
        if (input.checked) values.push(input.value)
      } else {
        if (input.value !== "") values.push(input.value)
      }
    })
    return values
  }

  #show(card) {
    if (card.pendingHide) {
      card.removeEventListener("transitionend", card.pendingHide)
      card.pendingHide = null
    }
    if (!card.classList.contains("hidden") && !card.classList.contains("opacity-0")) return
    card.classList.remove("hidden")
    requestAnimationFrame(() => card.classList.remove("opacity-0", "translate-y-2"))
    card.querySelectorAll("[data-condition-disabled]").forEach(el => {
      el.disabled = false
      delete el.dataset.conditionDisabled
    })
    card.querySelectorAll("[data-required]").forEach(el => el.required = true)
  }

  #hide(card) {
    card.querySelectorAll("input, select, textarea").forEach(el => {
      if (el.disabled) return
      el.disabled = true
      el.dataset.conditionDisabled = "true"
    })
    card.querySelectorAll("[data-required]").forEach(el => el.required = false)
    if (card.classList.contains("hidden")) return
    card.classList.add("opacity-0", "translate-y-2")
    card.pendingHide = () => {
      card.classList.add("hidden")
      card.pendingHide = null
    }
    card.addEventListener("transitionend", card.pendingHide, { once: true })
  }
}
